import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Leaf, Truck, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { EBTWICBanner } from '../components/layout/EBTWICBanner';
import { HeroSection } from '../components/sections/HeroSection';
import { ProductCard } from '../components/product/ProductCard';
import { RecipeBundle } from '../components/product/RecipeBundle';
import { mockProducts, recipeBundles } from '../data/mockProducts';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
export function HomePage() {
  const featuredProducts = mockProducts.slice(0, 8);
  const wicProducts = mockProducts.filter(p => p.isWIC).slice(0, 4);
  const bundles = recipeBundles.map(bundle => ({
    ...bundle,
    products: bundle.productIds.map(id => mockProducts.find(p => p.id === id)).filter(Boolean) as typeof mockProducts,
    totalPrice: bundle.productIds.reduce((sum, id) => {
      const product = mockProducts.find(p => p.id === id);
      return sum + (product?.price || 0);
    }, 0)
  }));
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-327">
      <EBTWICBanner data-id="element-328" />
      <TopNav data-id="element-329" />

      <main className="pb-24 md:pb-8" data-id="element-330">
        <HeroSection data-id="element-331" />

        {/* Value Props */}
        <section className="py-8 md:py-12 bg-white border-b border-gray-100" data-id="element-332">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-333">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-334">
              {[{
              icon: Leaf,
              title: 'Fresh & Authentic',
              description: 'Direct from trusted suppliers in Africa & Caribbean'
            }, {
              icon: Truck,
              title: 'Free Delivery',
              description: 'On orders over $35 • Same-day available'
            }, {
              icon: CreditCard,
              title: 'WIC & EBT Accepted',
              description: 'Easy checkout with government benefits'
            }].map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-4 p-4" data-id="element-335">
                  <div className="w-12 h-12 bg-[#10b981]/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-336">
                    <item.icon className="w-6 h-6 text-[#10b981]" data-id="element-337" />
                  </div>
                  <div data-id="element-338">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-339">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-340">{item.description}</p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Featured Products */}
        <section className="py-12 md:py-16" data-id="element-341">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-342">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex items-end justify-between mb-8" data-id="element-343">
              <div data-id="element-344">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-345">
                  Featured Products
                </h2>
                <p className="text-gray-600" data-id="element-346">
                  Popular items from our community
                </p>
              </div>
              <Link to="/search" className="hidden md:flex items-center gap-2 text-[#10b981] font-semibold hover:gap-3 transition-all" data-id="element-347">
                View All
                <ArrowRight className="w-4 h-4" data-id="element-348" />
              </Link>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-349">
              {featuredProducts.map((product, index) => <ProductCard key={product.id} product={product} index={index} data-id="element-350" />)}
            </div>

            <Link to="/search" className="md:hidden mt-6 flex items-center justify-center gap-2 text-[#10b981] font-semibold py-3" data-id="element-351">
              View All Products
              <ArrowRight className="w-4 h-4" data-id="element-352" />
            </Link>
          </div>
        </section>

        {/* Recipe Bundles */}
        <section className="py-12 md:py-16 bg-white" data-id="element-353">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-354">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-10" data-id="element-355">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-356">
                Recipe Bundles
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto" data-id="element-357">
                Everything you need for your favorite dishes, bundled together
                for savings
              </p>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-358">
              {bundles.map((bundle, index) => <motion.div key={bundle.id} variants={itemVariants} data-id="element-359">
                  <RecipeBundle title={bundle.title} description={bundle.description} products={bundle.products} totalPrice={bundle.totalPrice - (bundle.savings || 0)} savings={bundle.savings} data-id="element-360" />
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* WIC Approved Section */}
        <section className="py-12 md:py-16" data-id="element-361">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-362">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-6 md:p-10 border border-emerald-100" data-id="element-363">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8" data-id="element-364">
                <div data-id="element-365">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-bold mb-3" data-id="element-366">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full" data-id="element-367" />
                    WIC Approved
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-368">
                    Shop with WIC Benefits
                  </h2>
                  <p className="text-gray-600" data-id="element-369">
                    Quality products approved for WIC participants
                  </p>
                </div>
                <Link to="/wic-ebt-guide" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold transition-colors" data-id="element-370">
                  Learn More
                  <ArrowRight className="w-4 h-4" data-id="element-371" />
                </Link>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-372">
                {wicProducts.map((product, index) => <ProductCard key={product.id} product={product} index={index} data-id="element-373" />)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-[#1E3A5F]" data-id="element-374">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center" data-id="element-375">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-376">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-377">
                Ready to Shop?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto" data-id="element-378">
                Browse our full selection of authentic African and Caribbean
                groceries
              </p>
              <Link to="/search" className="inline-flex items-center gap-2 bg-[#F4A261] hover:bg-[#e8955a] text-[#1E3A5F] px-8 py-4 rounded-xl font-bold text-lg transition-colors" data-id="element-379">
                Start Shopping
                <ArrowRight className="w-5 h-5" data-id="element-380" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <BottomNav data-id="element-381" />
    </div>;
}